/**
 * Analytics — Metrics API Contracts
 *
 * Wire format for MetricsController endpoints.
 */

import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Values match the MetricType enum stored in the database (lowercase snake_case)
const METRIC_TYPES = [
  'ticket_sales',
  'visitor_count',
  'revenue',
  'attraction_rides',
  'wristband_activations',
  'payment_transactions',
  'points_awarded',
  'points_redeemed',
] as const;

// ---------------------------------------------------------------------------
// Query schemas
// ---------------------------------------------------------------------------

export const GetDailyMetricsQuerySchema = z.object({
  metricType: z.enum(METRIC_TYPES),
  startDate: z.string().regex(DATE_REGEX),
  endDate: z.string().regex(DATE_REGEX),
  dimension: z.string().optional(),
  dimensionValue: z.string().optional(),
});
export type GetDailyMetricsQuery = z.infer<typeof GetDailyMetricsQuerySchema>;

export const GetHourlyMetricsQuerySchema = z.object({
  metricType: z.enum(METRIC_TYPES),
  date: z.string().regex(DATE_REGEX),
});
export type GetHourlyMetricsQuery = z.infer<typeof GetHourlyMetricsQuerySchema>;

export const GetVisitorFlowQuerySchema = z.object({
  date: z.string().regex(DATE_REGEX),
});
export type GetVisitorFlowQuery = z.infer<typeof GetVisitorFlowQuerySchema>;

export const GetRevenueQuerySchema = z.object({
  startDate: z.string().regex(DATE_REGEX),
  endDate: z.string().regex(DATE_REGEX),
});
export type GetRevenueQuery = z.infer<typeof GetRevenueQuerySchema>;

export const GetCapacityQuerySchema = z.object({
  zone: z.string().min(1),
  hours: z.coerce.number().int().min(1).max(168).default(24),
});
export type GetCapacityQuery = z.infer<typeof GetCapacityQuerySchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const MetricSummarySchema = z.object({
  date: z.string().regex(DATE_REGEX),
  metricType: z.string(),
  dimension: z.string().nullable().optional(),
  dimensionValue: z.string().nullable().optional(),
  value: z.number(),
  count: z.number().int(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  avgValue: z.number().optional(),
});
export type MetricSummary = z.infer<typeof MetricSummarySchema>;

export const HourlyStatItemSchema = z.object({
  hour: z.string(), // ISO datetime
  metricType: z.string(),
  dimension: z.string().nullable().optional(),
  dimensionValue: z.string().nullable().optional(),
  value: z.string(), // Prisma BigInt serialized as string
  count: z.number().int(),
});
export type HourlyStatItem = z.infer<typeof HourlyStatItemSchema>;

export const VisitorFlowHourlyItemSchema = z.object({
  hour: z.number().int().min(0).max(23),
  entries: z.number().int(),
  exits: z.number().int(),
});
export const VisitorFlowGateItemSchema = z.object({
  gateId: z.string(),
  gateName: z.string(),
  entries: z.number().int(),
  exits: z.number().int(),
});
export const VisitorFlowSummarySchema = z.object({
  date: z.string(),
  totalEntries: z.number().int(),
  totalExits: z.number().int(),
  currentInPark: z.number().int(),
  hourlyFlow: z.array(VisitorFlowHourlyItemSchema),
  byGate: z.array(VisitorFlowGateItemSchema),
});
export type VisitorFlowSummary = z.infer<typeof VisitorFlowSummarySchema>;

export const RevenueSummaryChannelItemSchema = z.object({
  channel: z.string(),
  grossRevenue: z.number(),
  netRevenue: z.number(),
  transactions: z.number().int(),
});
export const RevenueSummaryDailyItemSchema = z.object({
  date: z.string().regex(DATE_REGEX),
  grossRevenue: z.number(),
  netRevenue: z.number(),
  transactions: z.number().int(),
});
export const RevenueSummaryResultSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  totals: z.object({
    transactionCount: z.number().int(),
    ticketCount: z.number().int(),
    grossRevenue: z.number(),
    discount: z.number(),
    tax: z.number(),
    netRevenue: z.number(),
    refundCount: z.number().int(),
    refundAmount: z.number(),
  }),
  byChannel: z.array(RevenueSummaryChannelItemSchema),
  daily: z.array(RevenueSummaryDailyItemSchema),
});
export type RevenueSummaryResult = z.infer<typeof RevenueSummaryResultSchema>;

export const MetricsCapacitySnapshotSchema = z.object({
  timestamp: z.string(),
  zone: z.string(),
  currentCount: z.number().int(),
  maxCapacity: z.number().int(),
  utilization: z.number(),
  queueLength: z.number().int(),
  avgWaitMinutes: z.number().optional(),
});
export type MetricsCapacitySnapshot = z.infer<typeof MetricsCapacitySnapshotSchema>;

export const RealtimeVisitorCountSchema = z.object({
  currentInPark: z.number().int(),
  updatedAt: z.string(),
});
export type RealtimeVisitorCount = z.infer<typeof RealtimeVisitorCountSchema>;
